/**
 * Exams Module
 * View available interactive exams and exam history
 * Exams are taken via voice conversation with LAMED
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';

interface Course {
  id: number;
  code: string;
  name: string;
  icon: string;
  category: string;
  topic_count: number;
  attempts: number;
  best_score: number | null;
  avg_score: number | null;
}

interface ExamAttempt {
  id: number;
  course_code: string;
  course_name: string;
  course_icon?: string;
  status: string;
  total_questions: number;
  correct_answers: number;
  score_percent: number;
  started_at: string;
  completed_at?: string;
  time_spent_seconds: number;
}

type ViewMode = 'available' | 'history';

const ExamsModule: React.FC = () => {
  const navigate = useNavigate();

  const [view, setView] = useState<ViewMode>('available');
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
  const [examHistory, setExamHistory] = useState<ExamAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch available courses for exams
  const fetchAvailableExams = useCallback(async () => {
    try {
      const response = await apiClient.get('/exams/available');
      setAvailableCourses(response.data);
    } catch (err) {
      console.error('Failed to fetch available exams:', err);
      setError('Failed to load available exams');
    }
  }, []);

  // Fetch exam history
  const fetchExamHistory = useCallback(async () => {
    try {
      const response = await apiClient.get('/exams/history');
      setExamHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch exam history:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAvailableExams(), fetchExamHistory()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAvailableExams, fetchExamHistory]);

  // Format time
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get score color
  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };


  // Render available exams view
  const renderAvailableExams = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-start gap-4">
          <div className="text-4xl">🎙️</div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Voice-Based Exams</h3>
            <p className="text-gray-600 mb-3">
              Exams are conducted through voice conversation with LAMED. Simply say
              <span className="font-medium text-blue-600"> "I want to take an exam on [course name]"</span> to start.
            </p>
            <p className="text-sm text-gray-500">
              LAMED will ask you questions verbally, and you answer by speaking.
              Your understanding is evaluated based on your explanations.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableCourses.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-100"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="text-3xl">{course.icon}</div>
              <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                {course.topic_count} topics
              </span>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mb-1">{course.name}</h3>
            <p className="text-sm text-gray-500 mb-4">{course.category}</p>

            {course.attempts > 0 ? (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Attempts:</span>
                  <span className="font-medium">{course.attempts}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Best Score:</span>
                  <span className={`font-medium ${getScoreColor(course.best_score || 0)}`}>
                    {course.best_score?.toFixed(1)}%
                  </span>
                </div>
                {course.avg_score && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Average:</span>
                    <span className="text-gray-700">{course.avg_score?.toFixed(1)}%</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600 text-center">
                  No attempts yet
                </p>
              </div>
            )}

            <button
              onClick={() => navigate('/voice')}
              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              Start Interactive Exam
            </button>
          </div>
        ))}
      </div>

      {availableCourses.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📚</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No courses available</h3>
          <p className="text-gray-500">Check back later for available exams</p>
        </div>
      )}
    </div>
  );

  // Render exam history view
  const renderHistory = () => {
    const completedExams = examHistory.filter(e => e.status === 'completed');
    const inProgressExams = examHistory.filter(e => e.status === 'in_progress');

    return (
      <div className="space-y-6">
        {/* In Progress Exams */}
        {inProgressExams.length > 0 && (
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center gap-2">
              <span>⏳</span> Exams In Progress
            </h3>
            <div className="space-y-2">
              {inProgressExams.map((attempt) => (
                <div
                  key={attempt.id}
                  className="bg-white rounded-lg p-4 flex justify-between items-center"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{attempt.course_icon}</span>
                    <div>
                      <p className="font-medium text-gray-800">{attempt.course_name}</p>
                      <p className="text-sm text-gray-500">Started {formatDate(attempt.started_at)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/voice')}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    Continue
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Exams */}
        <h3 className="text-xl font-semibold text-gray-800">Exam Results</h3>

        {completedExams.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <div className="text-4xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">No completed exams yet</h3>
            <p className="text-gray-500 mb-4">Take your first interactive exam to see your results here</p>
            <button
              onClick={() => setView('available')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Available Exams
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {completedExams.map((attempt) => {
              const passed = attempt.score_percent >= 70;
              return (
                <div
                  key={attempt.id}
                  className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">{attempt.course_icon}</div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{attempt.course_name}</h3>
                        <p className="text-sm text-gray-500">{formatDate(attempt.started_at)}</p>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getScoreColor(attempt.score_percent)}`}>
                        {attempt.score_percent.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-500">
                        {attempt.correct_answers}/{attempt.total_questions} correct
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatTime(attempt.time_spent_seconds)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {passed ? '✓ Passed' : '✗ Needs Practice'}
                      </span>
                    </div>
                  </div>

                  {/* Score bar */}
                  <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        passed ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${attempt.score_percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Voice"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Interactive Exams</h1>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setView('available')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  view === 'available' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Available
              </button>
              <button
                onClick={() => setView('history')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  view === 'history' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Results ({examHistory.filter(e => e.status === 'completed').length})
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
            <button
              onClick={() => setError(null)}
              className="float-right text-red-500 hover:text-red-700"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-6">
        {view === 'available' && renderAvailableExams()}
        {view === 'history' && renderHistory()}
      </div>
    </div>
  );
};

export default ExamsModule;
